/** @jsxImportSource @emotion/react */
import { useOutsideClick } from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { SerializedStyles } from "@emotion/utils";
import { useRouter } from "next/router";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isIPad13, isSafari } from "react-device-detect";
import { GrUserAdmin } from "react-icons/gr";
import ReactTooltip from "react-tooltip";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { ContactApi } from "../api/Contacts";
import { readAllLabels } from "../api/Label";
import { getAllUserSavedSearches } from "../api/SavedSearches";
import { ActiveEmailAtom } from "../atoms/ActiveEmailAtom";
import { CampaignUnsavedChangesAtom } from "../atoms/CampaignUnsavedChangesAtom";
import { LabelAtom } from "../atoms/LabelAtom";
import { RecipientAtom } from "../atoms/RecipientAtom";
import { CountedSavedSearchesAtom } from "../atoms/SavedSearches";
import { InternalUserAtom, UserAtom } from "../atoms/UserAtom";
import useMediaQuery from "../hook/useMediaQuery";
import { useResetAllRecoil } from "../hook/useResetAllRecoil";
import { customTooltip, notDraggable, notSelect } from "../styles/general";
import { localStorageClear, localStorageGetItem } from "../utils/LocalStorage";
import { useUnsavedChangesBlocker } from "../utils/UnsavedChangesBlocker";
import AvatarIcon from "./Avatar";
import IconBerserkerMail from "./Icon";
import LogoutPopover from "./LogoutPopover";
import { CurrentRecipientTagsAtom } from "./NewEmail/Modals/Recipients";

const IpadSafariStyles = css`
  padding: 32px 10px 64px !important;
`;

const MacSafariStyles = css`
  padding: 16px 10px !important;
`;

const customTooltipCSS = css`
  padding: 0 !important;
  opacity: 1 !important;
  box-shadow: rgba(0, 0, 0, 0.08) 0px 10px 20px, rgba(0, 0, 0, 0.16) 0px 6px 6px !important;
  border-radius: 8px !important;

  .tooltip-title {
    padding: 16px 24px;

    font-size: 16px;
    font-weight: 600;
    color: #222;

    background: #f9f9f9;
  }

  .tooltip-content {
    padding: 16px 24px;

    font-size: 16px;
    font-weight: 600;
    color: #222;

    display: flex;
    flex-direction: column;

    border-top: 1px solid #dddddd;

    > div + div {
      margin-top: 8px;
    }
  }

  .tooltip-row {
    display: flex;
    align-items: center;
    justify-content: start;

    grid-gap: 8px;

    img {
      width: 20px;
      height: 20px;
    }

    span {
      line-height: 20px;
    }

    &.grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);

      img,
      .result {
        justify-self: end;
      }

      img {
        margin-right: 0px;
      }
    }
  }

  .result {
    font-size: 15px;
    font-weight: 400;
  }
`;

const SidebarWrapper = styled.div`
  max-width: 60px;
  height: 100vh;

  position: absolute;
  z-index: 999 !important;
  top: 0;

  background: #ffffff;
  border-right: 1px solid #dddddd;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  padding: 10px;

  ${notSelect}
  ${notDraggable}

  &.opened {
    left: 0;
  }

  &.closed {
    left: -60px;
  }

  transition: left 0.3s ease-in-out;

  @media screen and (max-width: 1025px) {
    padding: 24px 10px;

    height: 90vh;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  bottom: 40px;
  left: 10px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SidebarIconsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  > div + div {
    margin-top: 32px;
  }

  @media (max-height: 700px) {
    > div + div {
      margin-top: 24px;
    }
  }
`;

const SidebarAvatarWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  cursor: pointer;
`;

interface IconDiv {
  name: string;
}

const IconDiv = styled.div<IconDiv>`
  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  img {
    filter: grayscale(1);
  }

  &:hover {
    img {
      filter: grayscale(0);
    }
  }
`;

export const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const IconContainer = styled.div``;

const SIDEBAR_BUTTONS_MOBILE = [
  {
    path: "/new-email",
    src: "/sidebar/New.svg",
    tooltipLabel: "New Email Broadcast",
  },
];

const SIDEBAR_BUTTONS = [
  {
    path: "/new-email",
    src: "/sidebar/New.svg",
    tooltipLabel: "New Email Broadcast",
  },
  {
    path: "/dashboard",
    src: "/sidebar/Dashboard.svg",
    tooltipLabel: "Dashboard",
  },
  {
    path: "/emails",
    src: "/sidebar/Emails.svg",
    tooltipLabel: "All Emails",
  },
  {
    path: "/all-forms",
    src: "/sidebar/Forms.svg",
    tooltipLabel: "Forms",
  },
  {
    path: "/new-campaign",
    src: "/sidebar/Campaign.svg",
    tooltipLabel: "Campaigns",
  },
  {
    path: "/contacts",
    src: "/sidebar/Contacts.svg",
    tooltipLabel: "Contacts",
  },
  {
    path: "/tags",
    src: "/sidebar/Tags.svg",
    tooltipLabel: "Manage Tags",
  },
  {
    path: "/automation-triggers",
    src: "/sidebar/AutomationTriggers.svg",
    tooltipLabel: "Automation Triggers",
  },
  {
    path: "/settings",
    src: "/sidebar/Settings.svg",
    tooltipLabel: "Account Settings",
  },
];

interface SidebarProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
}

const SideBar = ({
  isSidebarOpen,
  setIsSidebarOpen,
}: SidebarProps): JSX.Element => {
  const user = useRecoilValue(UserAtom);
  const router = useRouter();
  const resetActiveEmail = useResetRecoilState(ActiveEmailAtom);
  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const [style, setStyle] = useState<SerializedStyles>();
  const resetRecoil = useResetAllRecoil();
  const resetRecipientAtom = useResetRecoilState(RecipientAtom);
  const resetCurrentRecipientAtom = useResetRecoilState(
    CurrentRecipientTagsAtom
  );
  const [adminAvatar, setAdminAvatar] = useState<null | string>(null);
  const internalUser = useRecoilValue(InternalUserAtom);
  const isLargerThan1025 = useMediaQuery("(min-width: 1025px)");
  const isLargerThan560 = useMediaQuery("(min-width: 560px)");

  const sidebarWrapperRef = useRef(null);

  useOutsideClick({
    ref: sidebarWrapperRef,
    handler: () => (isLargerThan560 ? {} : setIsSidebarOpen(false)),
  });

  const setLabels = useSetRecoilState(LabelAtom);
  const setCountedSavedSearches = useSetRecoilState(CountedSavedSearchesAtom);

  const { campaignUnsavedChangesBlocker } = useUnsavedChangesBlocker();

  const setUnsavedChanges = useSetRecoilState(CampaignUnsavedChangesAtom);

  useEffect(() => {
    if (!router.asPath.includes("create-campaign")) {
      setUnsavedChanges(false);
    }
  }, [router.asPath]);

  async function getSavedSearches() {
    const savedSearchesResponse = await getAllUserSavedSearches();

    if (savedSearchesResponse) {
      const tagQueries = savedSearchesResponse.map(({ search }) => search);
      const counts = await ContactApi.countContactsByTagQueries(tagQueries);

      const countedSavedSearchesResponse = savedSearchesResponse.map(
        (savedSearch, index) => {
          return {
            ...savedSearch,
            contactsCount: counts[index],
          };
        }
      );

      setCountedSavedSearches(countedSavedSearchesResponse);
    }
  }

  async function getLabels() {
    const labelsResponse = await readAllLabels();

    if (labelsResponse) {
      setLabels(labelsResponse);
    }
  }

  useEffect(() => {
    getSavedSearches();
    getLabels();
  }, []);

  // I'm able to get into my own account
  const isInternalUser = useMemo(
    () => !!internalUser && user?.phone !== internalUser?.phone,
    [user, internalUser]
  );

  function logout() {
    localStorageClear();
    resetRecoil();
    router.push("/");
  }

  useEffect(() => {
    if (localStorageGetItem("ADMIN_AVATAR")) {
      setAdminAvatar(localStorageGetItem("ADMIN_AVATAR") as string);
    }
  }, [user]);

  useEffect(() => {
    if (isIPad13 && !isLargerThan1025) {
      setStyle(IpadSafariStyles);
      return;
    }

    if (isSafari && isLargerThan1025) {
      setStyle(MacSafariStyles);
      return;
    }

    setStyle(css``);
  }, [isSafari, isIPad13]);

  const buttons = isLargerThan560 ? SIDEBAR_BUTTONS : SIDEBAR_BUTTONS_MOBILE;

  return (
    <SidebarWrapper
      ref={sidebarWrapperRef}
      className={isSidebarOpen ? "opened" : "closed"}
      css={style}
    >
      <IconBerserkerMail name="berserker-logo" width={40} height={35} />
      {/* IN PROGRESSING <IconBerserkerMail name="threelines" width={20} height={13} /> */}
      <SidebarIconsWrapper>
        {buttons.map((button) => (
          <IconContainer key={button.path} data-tip data-for={button.path}>
            <IconDiv
              name={button.src}
              onClick={() => {
                if (isLargerThan560) {
                  console.log("do nothing");
                } else {
                  setIsSidebarOpen(false);
                }

                if (router.pathname === button.path) return;

                campaignUnsavedChangesBlocker(() => {
                  if (
                    !(
                      router.pathname.includes("/new-email") ||
                      router.pathname.includes("/edit-email")
                    )
                  )
                    resetActiveEmail();

                  resetRecipientAtom();
                  resetCurrentRecipientAtom();
                  router.push(button.path);
                });
              }}
            >
              <img src={button.src} alt={button.path} />
            </IconDiv>
            <ReactTooltip
              id={button.path}
              place="right"
              effect="solid"
              backgroundColor="#333333"
              css={customTooltip}
            >
              <TooltipText>{button.tooltipLabel}</TooltipText>
            </ReactTooltip>
          </IconContainer>
        ))}
      </SidebarIconsWrapper>

      <div style={{ position: "relative", display: "flex" }}>
        {isInternalUser && (
          <>
            <IconWrapper
              css={css`
                opacity: 0.4;
                svg > path {
                  stroke: #000;
                }
              `}
              data-tip
              data-for={"managed-by-an-internal"}
            >
              <GrUserAdmin />
            </IconWrapper>

            <ReactTooltip
              id={"managed-by-an-internal"}
              place="right"
              effect="solid"
              backgroundColor="#333333"
              css={customTooltip}
            >
              <TooltipText>Currently managed by an internal user</TooltipText>
            </ReactTooltip>
          </>
        )}

        <SidebarAvatarWrapper onClick={() => setOpenDropView(!openDropView)}>
          <AvatarIcon
            image={adminAvatar || user?.avatar}
            size={32}
            userName={user?.firstName}
          />
        </SidebarAvatarWrapper>

        {openDropView && (
          <LogoutPopover closeDropView={() => setOpenDropView(false)} />
        )}
      </div>
    </SidebarWrapper>
  );
};

export default SideBar;
